import * as React from 'react';

import { withTranslation, WithTranslation } from 'react-i18next';
import {
    buildWorkOrderExportSettings,
    buildWorkOrderSearchBody,
    clearSelectedWorkOrderID,
    deleteWorkOrder,
    getFSEsWithWorkOrder,
    getWorkOrderCsv,
    getWorkOrders,
    setSelectedWorkOrderID,
    setTableFilter,
    toggleEditWorkOrderModal,
    updateWorkOrderSelection
} from '../../actions/manageWorkOrderActions';
import { getFSEUsers } from '../../actions/manageJobActions';
import {
    IcountryBE,
    Ioption,
    ItableFiltersReducer,
    Itile,
    Iuser,
    IWorkOrder
} from '../../models';

import { debounce, DebouncedFunc } from 'lodash';
import { FieldConfig } from 'react-reactive-form';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { RouteComponentProps } from 'react-router';
import { closeAllModals } from '../../actions/commonActions';
import { constants } from '../../constants/constants';
import {
    workOrderDateRangeUnumForFilter,
    workOrderStatusEnum,
    workOrderStatusEnumForFilter,
    workOrderTabEnum,
    workOrderTypesEnum,
    workOrderVendorsEnum
} from '../../models-enums';
import { IinitialState } from '../../reducers';
import { selectIsLoading } from '../../reducers/commonReducers';
import { getCountries } from '../../reducers/countriesReducer';
import { emptyTile, initialWorkOrder } from '../../reducers/initialState';
import { selectVisibleWorkOrdersByID } from '../../reducers/manageWorkOrderReducer';
import { Banner } from '../common/Banner';
import ExportToExcelButton, {
    ExportRequest
} from '../common/ExportToExcelButton';
import { FormUtil } from '../common/FormUtil';
import { capWords } from '../../helpers/stringHelpers';
import SearchTableForm from '../common/SearchTableForm';
import API from '../../constants/apiEndpoints';
import { ConfirmSelectJobContainer } from './ConfirmSelectJobContainer';
import { EditWorkOrderModal } from './EditWorkOrderModal';
import { WorkOrderBeaconTableContainer } from './WorkOrderBeaconTableContainer';
import { WorkOrderCloseModal } from './WorkOrderCloseModal';
import NewJobModal from './modals/NewJobModal';
import { getAllOpenJobsForWorkOrders } from '../../actions/manageJobActions';
import { getSAPJobMapping } from '../../actions/commonActions';

type Iprops = RouteComponentProps<any>;

interface IdispatchProps {
    tableData: IWorkOrder[];
    totalPages: number;
    showEditWorkOrderModal: boolean;
    getWorkOrders: typeof getWorkOrders;
    toggleEditWorkOrderModal: typeof toggleEditWorkOrderModal;
    deleteWorkOrder: typeof deleteWorkOrder;
    setTableFilter: typeof setTableFilter;
    tableFilters: ItableFiltersReducer;
    loading: boolean;
    setSelectedWorkOrderID: typeof setSelectedWorkOrderID;
    clearSelectedWorkOrderID: typeof clearSelectedWorkOrderID;
    getWorkOrderCsv: typeof getWorkOrderCsv;
    selectedFacilityID: string;
    selectedWorkOrderID: string;
    selectedWorkOrder: IWorkOrder;
    manageWorkOrderAdmin: boolean;
    manageWorkOrderPMP: boolean;
    manageCustomerWorkOrders: boolean;
    user: Iuser;
    userOptions: Ioption[];
    fsesWithWorkOrdersLoading: boolean;
    selection: string[];
    closeAllModals: typeof closeAllModals;
    updateWorkOrderSelection: typeof updateWorkOrderSelection;
    getFSEsWithWorkOrder: typeof getFSEsWithWorkOrder;
    getFSEUsers: typeof getFSEUsers;
    allCountries: IcountryBE[];
    getAllOpenJobsForWorkOrders: typeof getAllOpenJobsForWorkOrders;
    getSAPJobMapping: typeof getSAPJobMapping;
}

interface Istate {
    selectedRow: any;
    currentTile: Itile;
    searchFieldConfig: FieldConfig;
    showCloseNoteModal: boolean;
    showNewJobModal: boolean;
}

class ManageWorkOrder extends React.Component<
    Iprops & IdispatchProps & WithTranslation,
    Istate
> {
    private setTableFilterDebounced: DebouncedFunc<
        (formValues: { [key: string]: any }) => void
    >;
    /**
     * The SAP Work Order toggle's value from before Unassigned/Unknown SAP WO forced it
     * on, so switching that filter back off restores the user's own choice. Kept on the
     * instance rather than in the table filters: it is transient UI memory, and every
     * key in the filters is a candidate for the search and CSV export request bodies.
     */
    private sapWorkOrderBeforeUnassigned: boolean | undefined;
    static defaultProps = {
        selection: []
    };
    constructor(props: Iprops & IdispatchProps & WithTranslation) {
        super(props);
        this.state = {
            selectedRow: {},
            currentTile: emptyTile,
            searchFieldConfig: this.buildSearchControls(),
            showCloseNoteModal: false,
            showNewJobModal: false
        };
        this.setTableFilterDebounced = debounce(
            this.props.setTableFilter,
            constants.formDebounceTime
        );
    }

    componentDidMount(): void {
        this.setState({
            currentTile: constants.getTileByURL(this.props.location.pathname)
        });

        const defaultStatus = FormUtil.convertToSingleOption({
            id: workOrderStatusEnumForFilter.New.toString(),
            label: this.props.t('new')
        });

        const activeCountryOption: Ioption = {
            value: this.props.user.countryID || '',
            label:
                this.props.allCountries?.find(
                    c => c.id === this.props.user.countryID
                )?.name || ''
        };

        this.props.setTableFilter({
            workOrderType: workOrderTypesEnum.repair,
            vendor: workOrderVendorsEnum.beacon,
            tabKey: workOrderTabEnum.beacon,
            page: 1,
            statusFilter: defaultStatus,
            isDeleted: false,
            sapWorkOrder: true,
            unassignedSapWorkOrder: false,
            countryID: activeCountryOption
        });

        document.addEventListener(
            'updatedWorkOrders',
            this.handleUpdatedData,
            false
        );
        // Seed the FSE Name dropdown with the same Primary Country + SAP toggle
        // defaults applied to the grid above, so the two stay in sync on load.
        this.props.getFSEsWithWorkOrder(this.props.user.countryID, true);
        this.props.getFSEUsers();
        this.props.getAllOpenJobsForWorkOrders();
        this.props.getSAPJobMapping();

        this.props.i18n.on('languageChanged', () => {
            this.setState({
                searchFieldConfig: this.buildSearchControls()
            });
        });
    }

    componentDidUpdate(prevProps: Iprops & IdispatchProps) {
        if (
            prevProps.showEditWorkOrderModal !==
                this.props.showEditWorkOrderModal &&
            !this.props.showEditWorkOrderModal
        ) {
            this.setState({ selectedRow: null });
        }

        // check to see if tableFilters are exactly the same, tiggers even when the values are the same
        if (prevProps.tableFilters !== this.props.tableFilters) {
            this.props.getWorkOrders();

            // scroll top every time a filter changes
            const tableDiv = document.getElementsByClassName('rt-tbody');
            if (tableDiv && tableDiv.length) {
                tableDiv[0].scrollTop = 0;
            }
        }

        // Rebuild the filter controls whenever the FSE option set changes. Compare
        // members, not just length, since a country/SAP refetch can swap the list
        // for a different one of the same size — but short-circuit on length first
        // so the common (unchanged) update doesn't stringify the whole list.
        // userOptions is built with `fsesWithWorkOrders?.map(...)`, so it can be
        // undefined; default to [] before comparing to avoid a deref crash.
        const prevOptions = prevProps.userOptions || [];
        const currOptions = this.props.userOptions || [];
        const fseOptionsChanged =
            prevOptions.length !== currOptions.length ||
            prevOptions.some((o, i) => o.value !== currOptions[i].value);
        // Also rebuild when the FSE fetch starts/finishes so the dropdown's
        // disabled state (driven by fsesWithWorkOrdersLoading) stays in sync.
        const fseLoadingChanged =
            prevProps.fsesWithWorkOrdersLoading !==
            this.props.fsesWithWorkOrdersLoading;
        // And rebuild when Unassigned/Unknown SAP WO flips, which is what disables the
        // SAP Work Order toggle. Without this the lock is invisible on the common path:
        // SAP is on by default, so flipping this filter usually doesn't change the SAP
        // value and therefore doesn't trigger the FSE refetch that rebuilds above.
        const unassignedSapChanged =
            prevProps.tableFilters.unassignedSapWorkOrder !==
            this.props.tableFilters.unassignedSapWorkOrder;
        if (fseOptionsChanged || fseLoadingChanged || unassignedSapChanged) {
            this.setState({ searchFieldConfig: this.buildSearchControls() });
        }
    }

    componentWillUnmount() {
        document.removeEventListener(
            'updatedWorkOrders',
            this.handleUpdatedData,
            false
        );
        this.props.closeAllModals();
        this.props.setTableFilter({
            vendor: undefined,
            page: 1,
            assignedUser: undefined
        });
    }

    handleUpdatedData = () => {
        this.props.getWorkOrders();
        this.setState({ selectedRow: {} });
    };

    /*
     * Note that technicianToDo is not an actual filter.  it is only used to select which To Do List to download
     */
    buildSearchControls = (): FieldConfig => {
        const disabled = false;

        const activeCountryOption: Ioption = {
            value: this.props.user.countryID || '',
            label:
                this.props.allCountries?.find(
                    c => c.id === this.props.user.countryID
                )?.name || ''
        };
        const countriesOptions: Ioption[] = this.props.allCountries.map(c => ({
            label: c.name,
            value: c.id
        }));

        const defaultStatus = FormUtil.convertToSingleOption({
            id: workOrderStatusEnumForFilter.New.toString(),
            label: this.props.t('new')
        });
        const {
            search,
            statusFilter = defaultStatus,
            dateRange,
            tabKey,
            assignedUser,
            countryID: selectedCountry,
            sapWorkOrder = true,
            unassignedSapWorkOrder = false,
            isDeleted = false
        } = this.props.tableFilters;

        const searchPlaceholder =
            this.state && tabKey === 'beacon'
                ? 'searchNumberPlaceholderBeacon'
                : 'searchNumberPlaceholder';

        const mainSearchControls = {
            search: {
                render: FormUtil.TextInputWithoutValidation,
                meta: {
                    label: 'common:search',
                    colWidth: 3,
                    type: 'text',
                    placeholder: searchPlaceholder,
                    isClearable: true
                },
                formState: { value: search, disabled }
            },
            countryID: {
                options: {
                    validators: FormUtil.validators.requiredWithTrim
                },
                render: FormUtil.Select,
                meta: {
                    options: countriesOptions,
                    label: 'user:primaryCountry',
                    colWidth: 2,
                    placeholder: 'manageUserQueue:countrySearchPlaceholder',
                    name: 'country'
                },
                // Reflect the currently selected country (falling back to the user's
                // default) so a controls rebuild doesn't snap the dropdown back to it.
                formState: {
                    value: selectedCountry || activeCountryOption,
                    disabled
                }
            },
            statusFilter: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    options: FormUtil.convertEnumToOptions(
                        workOrderStatusEnumForFilter
                    ),
                    label: 'status',
                    colWidth: 2,
                    isClearable: false,
                    shouldTranslate: true
                },
                formState: { value: statusFilter, disabled }
            },
            dateRange: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    options: FormUtil.convertEnumToOptions(
                        workOrderDateRangeUnumForFilter
                    ),
                    label: 'dateRange',
                    colWidth: 2,
                    isClearable: true,
                    shouldTranslate: true
                },
                formState: { value: dateRange, disabled }
            },
            fseUser: {
                render: FormUtil.SelectWithoutValidation,
                meta: {
                    options: this.props.userOptions,
                    label: 'FSENameTableHeader',
                    colWidth: 3,
                    isClearable: true,
                    shouldTranslate: true,
                    dataType: 'fseUser'
                },
                // Disable while the option data is in flight so the user can't
                // pick from a stale/empty list before the latest FSEs load.
                formState: {
                    value: assignedUser || '',
                    disabled: disabled || this.props.fsesWithWorkOrdersLoading
                }
            },
            sapWorkOrder: {
                render: FormUtil.Toggle,
                meta: {
                    label: 'sapWorkOrder',
                    colWidth: 2,
                    innerStyle: {
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        alignItems: 'center'
                    }
                },
                // Reflect the toggle's current store value so a controls rebuild
                // (triggered by a country/SAP refetch) doesn't snap it back to its
                // default. Same fix as the Primary Country dropdown above.
                //
                // Locked on while Unassigned/Unknown SAP WO is enabled: that filter is
                // meaningless for MyMedGas-sourced work orders, so rather than let the
                // user build a combination that can only ever return nothing, show why
                // it can't be changed. onSearchValueChanges restores the prior value
                // when the unassigned toggle goes back off.
                formState: {
                    value: sapWorkOrder,
                    disabled: disabled || unassignedSapWorkOrder
                }
            },
            isDeleted: {
                render: FormUtil.Toggle,
                meta: {
                    label: 'showDeleted',
                    colWidth: 2,
                    innerStyle: {
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        alignItems: 'center'
                    }
                },
                // Reflect the current store value (see sapWorkOrder above) so a
                // controls rebuild doesn't reset the Show Deleted toggle.
                formState: {
                    value: isDeleted,
                    disabled
                }
            },
            // Wider than its neighbours: the label is a phrase, not two words, and
            // wrapping it would break the toggle row's alignment.
            unassignedSapWorkOrder: {
                render: FormUtil.Toggle,
                meta: {
                    label: 'unassignedSapWorkOrder',
                    colWidth: 3,
                    innerStyle: {
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        alignItems: 'center'
                    }
                },
                // Reflect the current store value (see sapWorkOrder above).
                formState: {
                    value: unassignedSapWorkOrder,
                    disabled
                }
            },
            seperator: {
                render: FormUtil.TextLabel,
                meta: {
                    colWidth: 1
                }
            }
        };

        const searchFieldConfig = {
            controls: { ...mainSearchControls }
        } as FieldConfig;
        return searchFieldConfig;
    };

    downloadCsv(tech: Ioption | null) {
        const { t } = this.props;
        if (tech && tech.value) {
            this.props.getWorkOrderCsv(tech.value);
        } else {
            toastr.warning(
                t('common:warning'),
                t('Please select a technician.'),
                constants.toastrWarning
            );
        }
    }

    /*
     * Builds the CSV export request at click time so it always reflects the
     * filters the user currently has applied. Column headers are the same
     * translated labels shown on the grid (parent columns then the appended
     * PM task child column); the server keeps the field set/order fixed.
     */
    buildExportRequest = (): ExportRequest => {
        const { t } = this.props;
        return {
            url: API.workOrder.exportCsv,
            body: {
                ...buildWorkOrderSearchBody(
                    this.props.tableFilters,
                    this.props.selectedFacilityID
                ),
                // Translations still flow through t(); capWords() title-cases
                // each word so headers read consistently. Already-cased letters
                // and acronyms (FSE/WO/SAP) are left untouched.
                columnHeaders: [
                    capWords(t('activity')),
                    capWords(t('common:facility')),
                    capWords(t('productName')),
                    capWords(t('FSENameTableHeader')),
                    capWords(t('jobNumberTableHeader')),
                    capWords(t('jobType')),
                    capWords(t('woNumber')),
                    capWords(t('businessIndicator')),
                    capWords(t('status')),
                    capWords(t('dueDate')),
                    'Work Order ID'
                ],
                settings: buildWorkOrderExportSettings(t)
            },
            filename: 'work-orders.csv'
        };
    };

    /**
     * The FSE Name dropdown mirrors the grid's Primary Country + SAP toggle: only
     * FSEs with a (SAP or non-SAP) work order in that country are offered. Refetch
     * the options with the current country/toggle, drop any now-invalid FSE
     * selection, and commit the changed filter immediately (NOT debounced): the
     * refetch rebuilds the controls when it returns and that rebuild reads the
     * filters from the store, so a debounced commit could lose the race and snap
     * the dropdown back to its default.
     */
    refreshFseDropdown = (
        countryID: string | null,
        sapWorkOrder: boolean | null | undefined,
        filterDelta: { [key: string]: any }
    ) => {
        // Commit any pending debounced edit (Show Deleted / Search / Status /
        // Date Range) BEFORE the refetch. The refetch synchronously flips the FSE
        // loading flag, which rebuilds the controls from the store; without this
        // flush that rebuild would read the pre-debounce store value and snap the
        // in-flight control back to its last committed value.
        this.setTableFilterDebounced.flush();
        this.props.getFSEsWithWorkOrder(countryID, sapWorkOrder);
        this.props.setTableFilter({
            ...filterDelta,
            assignedUser: undefined,
            page: 1
        });
    };

    /**
     * Commits the Unassigned/Unknown SAP WO toggle. The filter only means anything for
     * SAP-sourced work orders, so enabling it forces the SAP Work Order toggle on (the
     * control renders disabled while that holds) and disabling it hands the user back
     * whatever they had before, rather than silently leaving them on the forced value.
     *
     * Only routes through refreshFseDropdown when the SAP toggle actually moves, since
     * that clears the FSE Name selection. SAP is on by default, so the common case is a
     * plain filter change and the user keeps the FSE they picked - which the ticket
     * requires, this toggle being combinable with every other filter.
     */
    setUnassignedSapWorkOrder = (enabled: boolean) => {
        const { sapWorkOrder } = this.props.tableFilters;

        if (enabled) {
            this.sapWorkOrderBeforeUnassigned = sapWorkOrder;
        }
        const nextSapWorkOrder = enabled
            ? true
            : this.sapWorkOrderBeforeUnassigned ?? sapWorkOrder;

        const filterDelta = {
            unassignedSapWorkOrder: enabled,
            sapWorkOrder: nextSapWorkOrder
        };

        if (nextSapWorkOrder !== sapWorkOrder) {
            this.refreshFseDropdown(
                this.props.tableFilters.countryID
                    ? this.props.tableFilters.countryID.value
                    : null,
                nextSapWorkOrder,
                filterDelta
            );
            return;
        }

        this.setTableFilterDebounced({ ...filterDelta, page: 1 });
    };

    onSearchValueChanges = (value: any, key: string) => {
        switch (key) {
            case 'search':
                this.setTableFilterDebounced({ search: value, page: 1 });
                break;
            case 'statusFilter':
                this.setTableFilterDebounced({
                    statusFilter: value ? value : null,
                    page: 1
                });
                break;
            case 'countryID':
                this.refreshFseDropdown(
                    value ? value.value : null,
                    this.props.tableFilters.sapWorkOrder,
                    { countryID: value ? value : null }
                );
                break;
            case 'isDeleted':
                this.setTableFilterDebounced({
                    isDeleted: value,
                    page: 1
                });
                break;
            case 'sapWorkOrder':
                this.refreshFseDropdown(
                    this.props.tableFilters.countryID
                        ? this.props.tableFilters.countryID.value
                        : null,
                    value,
                    { sapWorkOrder: value }
                );
                break;
            case 'unassignedSapWorkOrder':
                this.setUnassignedSapWorkOrder(value);
                break;
            case 'dateRange':
                this.setTableFilterDebounced({
                    dateRange: value ? value : null,
                    page: 1
                });
                break;
            case 'fseUser':
                this.setTableFilterDebounced({
                    assignedUser: value ? value : null,
                    page: 1
                });
                break;
            case 'technicianToDo':
                this.downloadCsv(value);
                this.setState({
                    searchFieldConfig: this.buildSearchControls()
                }); // this clears the select
                // this.setTableFilterDebounced({ technicianToDo: value, page: 1 });
                break;
            default:
                break;
        }
    };

    handleEdit(row: any) {
        this.setState({ selectedRow: row.index });
        this.props.toggleEditWorkOrderModal();
        this.props.setSelectedWorkOrderID(row.original.id);
    }

    /*
     * Show the action button if the work order is new or reopened
     * hide the action button if on the Repair tab and the work order is assigned to Beacon. These should be closed on the beacon tab only
     * we will need to stop hiding the button when not on the Beacon tab once we add more actions.  Might hide the close option from the dropdown in that case.d
     */
    shouldShowActionButton = (workOrder: IWorkOrder) => {
        return (
            (workOrder.status === workOrderStatusEnum.reopened ||
                workOrder.status === workOrderStatusEnum.new) &&
            (this.props.tableFilters.tabKey === workOrderTabEnum.repair &&
                workOrder.vendor === workOrderVendorsEnum.beacon) === false &&
            workOrder.vendor !== workOrderVendorsEnum.beacon // TODO temporarily prevent closing beacon work orders
        );
    };

    // disabled on the PMP tab and hide the delete and save buttons.
    shouldEnableWorkOrderForm = () => {
        return this.props.tableFilters.tabKey === workOrderTabEnum.preventative;
    };

    render() {
        const { t, tableFilters } = this.props;
        const { tabKey = workOrderTabEnum.repair } = tableFilters;
        const colorButton =
            constants.colors[
                `${this.state.currentTile.color}Button` as keyof typeof constants.colors
            ];

        return (
            <div className="manage-work-order" data-tab={tabKey}>
                <Banner
                    title={t('bannerTitle')}
                    img={this.state.currentTile.srcBanner}
                    color={this.state.currentTile.color}
                />
                <SearchTableForm
                    fieldConfig={this.state.searchFieldConfig}
                    handleSubmit={this.props.getWorkOrders}
                    loading={this.props.loading}
                    colorButton={colorButton}
                    subscribeValueChanges={true}
                    onValueChanges={this.onSearchValueChanges}
                    t={this.props.t}
                    showSearchButton={false}
                    xs={9}
                    lg={10}
                >
                    <ExportToExcelButton
                        getExportRequest={this.buildExportRequest}
                        inline
                    />
                </SearchTableForm>

                <WorkOrderBeaconTableContainer
                    t={t}
                    colorButton={colorButton}
                    currentTile={this.state.currentTile}
                />

                <EditWorkOrderModal
                    colorButton={colorButton}
                    disabled={this.shouldEnableWorkOrderForm()}
                    shouldDisableVendorSelect={
                        this.props.tableFilters.tabKey ===
                        workOrderTabEnum.beacon
                    }
                    shouldHideTechnicianSelect={
                        this.props.tableFilters.tabKey !==
                        workOrderTabEnum.beacon
                    }
                />
                <ConfirmSelectJobContainer
                    history={this.props.history}
                    colorButton={colorButton}
                    showNewJobModal={() =>
                        this.setState({ showNewJobModal: true })
                    }
                />

                <NewJobModal
                    colorButton={colorButton}
                    showModal={this.state.showNewJobModal}
                    secondModal={true}
                    closeModal={(closeAll = false) => {
                        this.setState({ showNewJobModal: false });
                        if (closeAll) {
                            this.props.closeAllModals();
                        }
                    }}
                    onHide={() => this.setState({ showNewJobModal: false })}
                />

                <WorkOrderCloseModal colorButton={colorButton} />
            </div>
        );
    }
}

const mapStateToProps = (state: IinitialState) => {
    const tableData = selectVisibleWorkOrdersByID(state);
    const userOptions = state.manageWorkOrder.fsesWithWorkOrders
        ?.filter(Boolean)
        .map(user => {
            const name = `${user.first} ${user.last}`;
            return { label: name, value: user.id };
        });

    const manageWorkOrderAdmin = constants.hasSecurityFunction(state.user, [
        constants.securityFunctions.ManageWorkOrderAdmin.id
    ]);
    const manageWorkOrderPMP = constants.hasSecurityFunction(state.user, [
        constants.securityFunctions.ManageWorkOrderPMP.id
    ]);
    const manageCustomerWorkOrders = constants.hasSecurityFunction(state.user, [
        constants.securityFunctions.ManageCustomerWorkOrders.id
    ]);

    const selectedWorkOrder =
        state.manageWorkOrder.workOrdersByID[
            state.manageWorkOrder.selectedWorkOrderID
        ] || initialWorkOrder;

    const allCountries = getCountries(state);

    return {
        tableData,
        manageWorkOrderAdmin,
        manageWorkOrderPMP,
        manageCustomerWorkOrders,
        totalPages: state.manageWorkOrder.totalPages,
        showEditWorkOrderModal: state.manageWorkOrder.showEditWorkOrderModal,
        tableFilters: state.manageWorkOrder.tableFilters,
        selectedFacilityID: state.selectedFacilityID,
        selectedWorkOrderID: state.manageWorkOrder.selectedWorkOrderID,
        loading: selectIsLoading(state),
        selectedWorkOrder,
        user: state.user,
        userOptions,
        fsesWithWorkOrdersLoading:
            state.manageWorkOrder.fsesWithWorkOrdersLoading,
        allCountries
    };
};

export default withTranslation('manageWorkOrder')(
    connect(mapStateToProps, {
        getWorkOrders,
        toggleEditWorkOrderModal,
        deleteWorkOrder,
        setTableFilter,
        setSelectedWorkOrderID,
        clearSelectedWorkOrderID,
        getWorkOrderCsv,
        closeAllModals,
        updateWorkOrderSelection,
        getFSEsWithWorkOrder,
        getFSEUsers,
        getAllOpenJobsForWorkOrders,
        getSAPJobMapping
    })(ManageWorkOrder)
);
